import { DASHBOARD_COLUMNS } from '@clockwise/semantic-core';
import { type ReactNode, useCallback, useEffect, useRef } from 'react';
import { GridLayout, type Layout, useContainerWidth } from 'react-grid-layout';
// RGL's own stylesheet. Imported here rather than in `globals.css` so its
// selectors only load with the dashboard bundle and can never restyle a page
// that doesn't use a grid. Grid chrome (placeholder, resize handle, drag
// cursors) lives in the sibling stylesheet.
import 'react-grid-layout/css/styles.css';
import './dashboard-grid.css';

/** Class on a tile's header that makes it the drag handle. */
export const DRAG_HANDLE_CLASS = 'dashboard-drag-handle';

/**
 * Opt a subtree inside the drag handle back out of dragging — a tile's header
 * carries its toggles and buttons, and pressing one must not start a drag.
 */
export const DRAG_CANCEL_CLASS = 'dashboard-drag-cancel';

// `containerPadding: [0, 0]` matters: react-grid-layout otherwise defaults it
// to `margin`, insetting every tile by 16px on each side. That left the tiles
// visibly narrower than the KPI strip above them, which spans the page shell.
const GRID_CONFIG = {
  cols: DASHBOARD_COLUMNS,
  rowHeight: 30,
  margin: [16, 16],
  containerPadding: [0, 0],
} as const;
const DRAG_CONFIG = {
  bounded: true,
  handle: `.${DRAG_HANDLE_CLASS}`,
  cancel: `.${DRAG_CANCEL_CLASS}`,
} as const;
const RESIZE_CONFIG = { handles: ['se'] } as const;

interface Props {
  layout: Layout;
  onLayoutChange: (next: Layout) => void;
  children: ReactNode;
}

/**
 * The one configured react-grid-layout instance every dashboard renders into.
 *
 * Centralising it means a dashboard declares only *what* its tiles are and
 * where they start; column count, row height, gutters, drag affordances and
 * the resize/redraw plumbing are identical everywhere and can be changed in
 * one place. It also keeps all tiles in a **single** grid, so any tile can be
 * dragged relative to any other.
 *
 * Tiles are dragged by their header (`DRAG_HANDLE_CLASS`) rather than anywhere
 * on the card, so the controls and charts inside a tile stay clickable.
 */
export function DashboardGrid({ layout, onLayoutChange, children }: Props) {
  // RGL needs a pixel width — it can't size itself from CSS. `useContainerWidth`
  // measures the element `containerRef` is attached to and re-measures on resize.
  // `measureBeforeMount` matters: without it the hook renders once with its
  // default `initialWidth` (1280) and, if the container never subsequently
  // *changes* size, keeps it — leaving tiles a fixed 1280px wide and visibly
  // narrower than the KPI strip above them on any other viewport.
  const { width, containerRef, mounted } = useContainerWidth({ measureBeforeMount: true });

  // Recharts' ResponsiveContainer measures on window resize. A grid resize
  // changes a tile's box without one, so charts would keep their old width
  // until the next real resize — dispatch a synthetic event to re-measure.
  //
  // Coalesced to one dispatch per frame: RGL calls onResize on every pointer
  // move, and a window-level resize wakes *every* listener in the app (16
  // chart containers on this page, plus the shell), none of which care that a
  // single tile changed size.
  const pendingFrame = useRef<number | null>(null);
  const remeasureCharts = useCallback((): void => {
    if (pendingFrame.current !== null) return;
    pendingFrame.current = window.requestAnimationFrame(() => {
      pendingFrame.current = null;
      window.dispatchEvent(new Event('resize'));
    });
  }, []);

  useEffect(
    () => () => {
      if (pendingFrame.current !== null) window.cancelAnimationFrame(pendingFrame.current);
    },
    [],
  );

  // `onDragStop` doesn't reliably fire (dropping outside the window, or a drag
  // cancelled by the browser), which would leave the grabbing cursor stuck on
  // for the rest of the session. Clear it from the document instead.
  useEffect(() => {
    const clear = (): void => document.body.classList.remove('rgl-dragging');
    window.addEventListener('mouseup', clear);
    window.addEventListener('dragend', clear);
    window.addEventListener('blur', clear);
    return () => {
      window.removeEventListener('mouseup', clear);
      window.removeEventListener('dragend', clear);
      window.removeEventListener('blur', clear);
      clear();
    };
  }, []);

  return (
    <div ref={containerRef}>
      {/* Rendering before the first measurement would lay every tile out at
          width 0 and then visibly reflow. */}
      {mounted ? (
        <GridLayout
          className="dashboard-grid"
          width={width}
          layout={layout}
          gridConfig={GRID_CONFIG}
          dragConfig={DRAG_CONFIG}
          resizeConfig={RESIZE_CONFIG}
          onLayoutChange={onLayoutChange}
          onDragStart={() => document.body.classList.add('rgl-dragging')}
          onDragStop={() => document.body.classList.remove('rgl-dragging')}
          onResize={remeasureCharts}
          onResizeStop={remeasureCharts}
        >
          {children}
        </GridLayout>
      ) : null}
    </div>
  );
}
